import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const PaymentCheckout = () => {
  const { assessmentId: pathAssessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get assessment ID from path params or query params
  const assessmentId = pathAssessmentId || searchParams.get('assessment_id');
  
  // Get customer email from URL params or auth context
  const customerEmail = searchParams.get('email') || user?.email || '';

  // Log for debugging
  useEffect(() => {
    logger.log('PaymentCheckout mounted:', { assessmentId, customerEmail, pathAssessmentId, queryParam: searchParams.get('assessment_id') });
  }, [assessmentId, customerEmail]);

  // Payment package information
  const packageInfo = {
    name: "Professional Energy Savings Assessment Report",
    amount: 990,
    currency: "AUD",
    features: [
      "Complete professional ESA report with detailed recommendations",
      "Industry-specific RECC opportunities analysis", 
      "Implementation roadmaps and priority rankings",
      "Partner connections and funding opportunities",
      "Professional PDF export for executive review",
      "48-hour delivery guarantee",
      "90-day money-back guarantee"
    ]
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation: Ensure we have customer email
      if (!customerEmail) {
        throw new Error('Customer email is required for report delivery');
      }

      // Get current origin for dynamic URL building
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          package_id: 'esa_report',
          assessment_id: assessmentId,
          customer_email: customerEmail,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Payment initialization failed');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      setError(error.message);
      logger.error('Payment error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Complete Your Professional ESA Report
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Get your detailed energy savings analysis with implementation roadmaps
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Package Details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {packageInfo.name}
              </h2>
              <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
                ${packageInfo.amount} {packageInfo.currency}
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">One-time payment</p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-lg">What's Included:</h3>
              <ul className="space-y-3">
                {packageInfo.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Security & Guarantees */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Security & Guarantees</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">SSL Encrypted</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">48hr Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Money-Back</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Action */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Complete Your Purchase</h3>
            
            {assessmentId && (
              <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-300">Assessment Complete</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">Assessment ID: {assessmentId}</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-300">Payment Error</p>
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={loading}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors ${
                loading 
                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed text-white' 
                  : 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Initializing Payment...</span>
                </div>
              ) : (
                <span>Pay ${packageInfo.amount} {packageInfo.currency} - Secure Checkout</span>
              )}
            </button>

            {/* Payment Methods */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Secure payment powered by Stripe</p>
              <div className="flex items-center justify-center gap-4 opacity-80 dark:opacity-60">
                <div className="bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                  <img 
                    src="data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 0h22v24H1z' fill='%23005aa0'/%3E%3Cpath d='M4 8h16v1H4zm0 2h16v1H4zm0 2h16v1H4zm0 2h16v1H4z' fill='white'/%3E%3C/svg%3E" 
                    alt="Visa" 
                    className="h-8" 
                  />
                </div>
                <div className="bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                  <img 
                    src="data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='9' cy='12' r='7' fill='%23ea001b'/%3E%3Ccircle cx='15' cy='12' r='7' fill='%23ffa200'/%3E%3C/svg%3E" 
                    alt="Mastercard" 
                    className="h-8" 
                  />
                </div>
                <div className="bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                  <img 
                    src="data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 8h20v8H2z' fill='%230066cc'/%3E%3Cpath d='M6 10h4v4H6z' fill='white'/%3E%3C/svg%3E" 
                    alt="American Express" 
                    className="h-8" 
                  />
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Need help? Contact us at{' '}
                <a 
                  href="mailto:info@relyability.ai" 
                  className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline"
                >
                  info@relyability.ai
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Back Navigation */}
        <div className="text-center mt-12">
          <Link 
            to="/" 
            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;
